import React, { useState, useEffect, useMemo } from 'react';
import ReactFlow, {
    useNodesState,
    useEdgesState,
    ReactFlowProvider,
    Background,
    Controls,
    MiniMap,
    MarkerType,
} from 'reactflow';
import { Box } from '@mui/material';
import 'reactflow/dist/style.css';

// CSS for circular nodes
const nodeStyles = `
    .react-flow__node {
        border-radius: 50% !important;
    }
    .react-flow__node .react-flow__node-label {
        text-align: center;
        word-wrap: break-word;
        overflow-wrap: break-word;
        max-width: 100px; 
    }
`;

// Color palette for different node types
const getNodeColor = (type) => {
    const colors = {
        Entity: { bg: '#e3f2fd', border: '#1976d2' },
        SourceRecord: { bg: '#f3e5f5', border: '#7b1fa2' },
        GoldenRecord: { bg: '#e8f5e9', border: '#388e3c' },
        default: { bg: '#fff3e0', border: '#f57c00' },
    };
    return colors[type] || colors.default;
};

// Dummy nodes data
const initialNodes = [
    {
        id: '1',
        type: 'default',
        position: { x: 250, y: 100 },
        data: { label: 'Node 1' },
        style: {
            borderRadius: '50%',
            width: 120,
            height: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: getNodeColor('Entity').bg,
            border: `2px solid ${getNodeColor('Entity').border}`,
        },
    },
    {
        id: '2',
        type: 'default',
        position: { x: 500, y: 100 },
        data: { label: 'Node 2' },
        style: {
            borderRadius: '50%',
            width: 120,
            height: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: getNodeColor('SourceRecord').bg,
            border: `2px solid ${getNodeColor('SourceRecord').border}`,
        },
    },
    {
        id: '3',
        type: 'default',
        position: { x: 750, y: 100 },
        data: { label: 'Node 3' },
        style: {
            borderRadius: '50%',
            width: 120,
            height: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: getNodeColor('GoldenRecord').bg,
            border: `2px solid ${getNodeColor('GoldenRecord').border}`,
        },
    },
    {
        id: '4',
        type: 'default',
        position: { x: 375, y: 300 },
        data: { label: 'Node 4' },
        style: {
            borderRadius: '50%',
            width: 120,
            height: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: getNodeColor('Entity').bg,
            border: `2px solid ${getNodeColor('Entity').border}`,
        },
    },
    {
        id: '5',
        type: 'default',
        position: { x: 625, y: 300 },
        data: { label: 'Node 5' },
        style: {
            borderRadius: '50%',
            width: 120,
            height: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: getNodeColor('default').bg,
            border: `2px solid ${getNodeColor('default').border}`,
        },
    },
];

// Calculate edge positions based on node positions
const calculateEdgePositions = (edges, nodes) => {
    const nodePositions = new Map();
    nodes.forEach((node) => {
        nodePositions.set(node.id, node.position);
    });

    return edges.map((edge) => {
        const sourcePos = nodePositions.get(edge.source);
        const targetPos = nodePositions.get(edge.target);

        if (sourcePos && targetPos) {
            // Calculate angle from source to target
            const dx = targetPos.x - sourcePos.x;
            const dy = targetPos.y - sourcePos.y;
            const angle = Math.atan2(dy, dx);

            // Determine source position (where edge leaves source node)
            let sourcePosition = 'bottom';
            if (angle >= -Math.PI / 4 && angle < Math.PI / 4) {
                sourcePosition = 'right';
            } else if (angle >= Math.PI / 4 && angle < 3 * Math.PI / 4) {
                sourcePosition = 'bottom';
            } else if (angle >= 3 * Math.PI / 4 || angle < -3 * Math.PI / 4) {
                sourcePosition = 'left';
            } else {
                sourcePosition = 'top';
            }

            // Determine target position (where edge enters target node)
            // Calculate angle from target's perspective (reverse direction)
            const reverseAngle = Math.atan2(-dy, -dx);
            let targetPosition = 'top';
            if (reverseAngle >= -Math.PI / 4 && reverseAngle < Math.PI / 4) {
                targetPosition = 'right';
            } else if (reverseAngle >= Math.PI / 4 && reverseAngle < 3 * Math.PI / 4) {
                targetPosition = 'bottom';
            } else if (reverseAngle >= 3 * Math.PI / 4 || reverseAngle < -3 * Math.PI / 4) {
                targetPosition = 'left';
            } else {
                targetPosition = 'top';
            }

            return {
                ...edge,
                sourcePosition,
                targetPosition,
            };
        }

        return edge;
    });
};

// Dummy edges data (without positions - will be calculated dynamically)
const initialEdges = [
    {
        id: 'e1-2',
        source: '1',
        target: '2',
        label: 'has_lineage',
        type: 'straight',
        markerEnd: {
            type: MarkerType.ArrowClosed,
        },
        style: {
            strokeWidth: 2,
        },
    },
    {
        id: 'e2-3',
        source: '2',
        target: '3',
        label: 'has_lineage',
        type: 'straight',
        markerEnd: {
            type: MarkerType.ArrowClosed,
        },
        style: {
            strokeWidth: 2,
        },
    },
    {
        id: 'e1-4',
        source: '1',
        target: '4',
        label: 'related_to',
        type: 'straight',
        markerEnd: {
            type: MarkerType.ArrowClosed,
        },
        style: {
            strokeWidth: 2,
        },
    },
    {
        id: 'e4-5',
        source: '4',
        target: '5',
        label: 'related_to',
        type: 'straight',
        markerEnd: {
            type: MarkerType.ArrowClosed,
        },
        style: {
            strokeWidth: 2,
        },
    },
];

function NeoGraph() {
    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

    // Recalculate edge positions whenever nodes change (e.g., when dragged)
    useEffect(() => {
        // Get base edge structure (without positions) from initial edges
        const baseEdges = initialEdges.map(({ sourcePosition, targetPosition, ...rest }) => rest);
        const updatedEdges = calculateEdgePositions(baseEdges, nodes);
        setEdges(updatedEdges);
    }, [nodes, setEdges]);

    return (
        <>
            <style>{nodeStyles}</style>
            <Box
                sx={{
                    width: '100%',
                    height: '600px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    position: 'relative',
                }}
            >
                <ReactFlowProvider>
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        fitView
                        fitViewOptions={{ padding: 0.2 }}
                    >
                        <Background />
                        <Controls />
                        <MiniMap />
                    </ReactFlow>
                </ReactFlowProvider>
            </Box>
        </>
    );
}

export default NeoGraph;
